import React, { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { DEMO_URL, CONTACT_EMAIL, v2Path } from './links';
import { toMediaLang } from './media';
import { useReducedMotion } from './useReducedMotion';
import { SiteProvider, type Site } from './edition';
import './HomeV2.css';

/**
 * Website v2 (hofsync-org marketing/website-refresh/concept-2026-10.md). It has
 * its own header and footer so PublicLayout, and every page under it, stays
 * exactly as it is. HOF-662: it serves two sites, the homepage at `/` and the
 * noindex draft at /home_v2, each in its own edition (`edition.tsx`).
 */
const HomeV2Layout: React.FC<{ site: Site }> = ({ site }) => {
  const { t, i18n } = useTranslation();
  const { base, draft } = site;
  const home = base || '/';
  const lang = toMediaLang(i18n.language);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const firstRender = useRef(true);
  // A ref, not an effect dependency: a preference change must not re-scroll.
  const reduceMotion = useRef(false);
  reduceMotion.current = useReducedMotion();

  // A draft beside the live site: keep it out of search engines.
  useEffect(() => {
    if (!draft) return;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [draft]);

  // React Router does not scroll to `#anchor` targets on its own, nor back to
  // the top on a page change. The first render already starts at the top.
  useEffect(() => {
    setMenuOpen(false);
    const initial = firstRender.current;
    firstRender.current = false;
    if (location.hash) {
      // An explicit `smooth` overrides the reduced-motion CSS, so pick it here.
      document.getElementById(location.hash.slice(1))?.scrollIntoView?.({
        behavior: reduceMotion.current ? 'auto' : 'smooth',
      });
    } else if (!initial) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  return (
    <SiteProvider value={site}>
      <div className="hv2" lang={lang}>
        <header className="hv2-header">
          <div className="hv2-header__inner">
            <Link to={home} className="hv2-header__brand" aria-label={t('homeV2.nav.home')}>
              <img src="/hofsync_logo.gif" alt="" width={36} height={36} />
              <span>Hofsync</span>
            </Link>
            <button
              type="button"
              className="hv2-header__menu"
              aria-expanded={menuOpen}
              aria-controls="hv2-nav"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {t('homeV2.nav.menu')}
            </button>
            <nav id="hv2-nav" className={`hv2-nav${menuOpen ? ' hv2-nav--open' : ''}`} aria-label={t('homeV2.nav.label')}>
              <Link to={`${home}#so-funktionierts`}>{t('homeV2.nav.how')}</Link>
              <Link to={v2Path('company', lang, base)}>{t('homeV2.nav.company')}</Link>
              <Link to={v2Path('security', lang, base)}>{t('homeV2.nav.security')}</Link>
              <Link to="/login" className="hv2-nav__login">
                {t('homeV2.nav.login')}
              </Link>
              <a
                className="hv2-button hv2-button--primary hv2-nav__demo"
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('homeV2.nav.demo')}
              </a>
            </nav>
          </div>
        </header>

        <main className="hv2-main">
          <Outlet />
        </main>

        <footer className="hv2-footer hv2-dark">
          <div className="hv2-footer__inner">
            <div className="hv2-footer__brand">
              <img src="/hofsync_logo.gif" alt="" width={28} height={28} />
              <span>{t('homeV2.footer.copyright')}</span>
            </div>
            <nav className="hv2-footer__links" aria-label="Footer">
              <Link to={v2Path('company', lang, base)}>{t('homeV2.footer.company')}</Link>
              <Link to={v2Path('security', lang, base)}>{t('homeV2.footer.security')}</Link>
              <a href="/impressum">{t('homeV2.footer.impressum')}</a>
              <a href="/datenschutz">{t('homeV2.footer.privacy')}</a>
              <a href="/terms">{t('homeV2.footer.terms')}</a>
            </nav>
            <div className="hv2-footer__meta">
              <a href={`mailto:${CONTACT_EMAIL}`}>
                {t('homeV2.footer.contact')}: {CONTACT_EMAIL}
              </a>
              <label className="hv2-footer__lang">
                <span>{t('homeV2.footer.language')}</span>
                <select value={lang} onChange={(e) => i18n.changeLanguage(e.target.value)}>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </label>
            </div>
          </div>
        </footer>
      </div>
    </SiteProvider>
  );
};

export default HomeV2Layout;
